import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Icon } from '../common/Icon';
import { FaBrain, FaPlay, FaStop, FaPlus } from 'react-icons/fa';
import { BrainwaveOscillatorNode, BrainwaveControlPoint, ModulationType } from '../../utils/audio/BrainwaveOscillatorNode';
import { BrainwaveSettings } from '../../hooks/useWebAudio';

const GeneratorContainer = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #333;
`;

const GeneratorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const GeneratorTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
`;

const PreviewButton = styled.button<{ $isPlaying?: boolean }>`
  background: ${props => props.$isPlaying ? '#f44336' : '#4caf50'};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$isPlaying ? '#d32f2f' : '#45a049'};
  }
`;

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  margin-bottom: 24px;
`;

const PresetButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? '#e91e63' : '#2a2a2a'};
  color: white;
  border: 1px solid ${props => props.$active ? '#e91e63' : '#444'};
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
  text-align: center;

  &:hover {
    background: ${props => props.$active ? '#d81b60' : '#333'};
    border-color: ${props => props.$active ? '#d81b60' : '#555'};
  }

  small {
    display: block;
    font-size: 10px;
    opacity: 0.7;
    margin-top: 2px;
  }
`;

const ControlsSection = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const ControlGroup = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ControlLabel = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const ControlValue = styled.span`
  color: #e91e63;
  font-size: 12px;
  font-weight: 600;
`;

const Slider = styled.input<{ $isLive?: boolean }>`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: ${props => props.$isLive ? 'linear-gradient(45deg, #444, #555, #444)' : '#444'};
  background-size: ${props => props.$isLive ? '20px 20px' : 'auto'};
  animation: ${props => props.$isLive ? 'shimmer 2s linear infinite' : 'none'};
  outline: none;
  -webkit-appearance: none;
  
  @keyframes shimmer {
    0% { background-position: 0 0; }
    100% { background-position: 20px 0; }
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.$isLive ? '#ff4081' : '#e91e63'};
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: ${props => props.$isLive ? '0 0 8px rgba(255, 64, 129, 0.5)' : 'none'};
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.$isLive ? '#ff4081' : '#e91e63'};
    cursor: pointer;
    border: none;
    transition: all 0.2s;
    box-shadow: ${props => props.$isLive ? '0 0 8px rgba(255, 64, 129, 0.5)' : 'none'};
  }

  &::-moz-range-thumb:hover {
    transform: scale(1.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #444;
  border-radius: 4px;
  background: #1a1a1a;
  color: #ffffff;
  font-size: 13px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #e91e63;
  }
`;

const DurationInput = styled.input`
  width: 80px;
  padding: 6px 8px;
  border: 1px solid #444;
  border-radius: 4px;
  background: #1a1a1a;
  color: #ffffff;
  font-size: 12px;
  text-align: right;
  
  &:focus {
    outline: none;
    border-color: #e91e63;
  }
`;

const AddTrackButton = styled.button`
  width: 100%;
  background: #e91e63;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: #d81b60;
  }
`;

interface BrainwavePreset {
  name: string;
  carrierFreq: number;
  brainwaveFreq: number;
  description: string;
  modulationType: ModulationType;
}

const PRESETS: BrainwavePreset[] = [
  { name: 'Restoration', carrierFreq: 80, brainwaveFreq: 1.5, description: 'Delta waves', modulationType: 'binaural' },
  { name: 'Dream', carrierFreq: 120, brainwaveFreq: 3, description: 'Theta waves', modulationType: 'binaural' },
  { name: 'Twilight', carrierFreq: 120, brainwaveFreq: 6, description: 'Theta waves', modulationType: 'binaural' },
  { name: 'Relaxation', carrierFreq: 120, brainwaveFreq: 10, description: 'Alpha waves', modulationType: 'binaural' },
  { name: 'Focus', carrierFreq: 120, brainwaveFreq: 15, description: 'Beta waves', modulationType: 'binaural' },
  { name: 'Alertness', carrierFreq: 120, brainwaveFreq: 25, description: 'Beta waves', modulationType: 'binaural' },
  { name: 'Learning', carrierFreq: 120, brainwaveFreq: 40, description: 'Gamma pulses', modulationType: 'isochronic' }
];

interface BrainwaveGeneratorV2Props {
  audioContext: AudioContext | null;
  onAddTrack: (settings: BrainwaveSettings) => void;
  timelineDuration?: number;
}

export const BrainwaveGeneratorV2: React.FC<BrainwaveGeneratorV2Props> = ({
  audioContext,
  onAddTrack,
  timelineDuration
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string>('Twilight');
  const [carrierFreq, setCarrierFreq] = useState<number>(120);
  const [brainwaveFreq, setBrainwaveFreq] = useState<number>(6);
  const [amplitude, setAmplitude] = useState<number>(0.2);
  const [modulationType, setModulationType] = useState<ModulationType>('binaural');
  const [duration, setDuration] = useState<number>(timelineDuration || 300); // Use timeline duration or 5 minutes default
  const [isPreviewPlaying, setIsPreviewPlaying] = useState<boolean>(false);
  const [isManuallyEdited, setIsManuallyEdited] = useState<boolean>(false); // Track if user manually changed duration
  
  const previewOscillatorRef = useRef<BrainwaveOscillatorNode | null>(null);
  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPreview();
    };
  }, []);

  // Update duration when timeline duration changes (only if not manually edited)
  useEffect(() => {
    if (!isManuallyEdited && timelineDuration !== undefined && timelineDuration > 0) {
      setDuration(Math.ceil(timelineDuration)); // Round up to nearest second
    } else if (!isManuallyEdited && (!timelineDuration || timelineDuration === 0)) {
      setDuration(300); // Default to 5 minutes if timeline is empty
    }
  }, [timelineDuration, isManuallyEdited]);


  const stopPreview = useCallback(() => {
    if (previewOscillatorRef.current) {
      previewOscillatorRef.current.stop();
      previewOscillatorRef.current.disconnect();
      previewOscillatorRef.current = null;
    }

    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }

    setIsPreviewPlaying(false);
  }, []);

  const handlePresetSelect = useCallback((preset: BrainwavePreset) => {
    setSelectedPreset(preset.name);
    setCarrierFreq(preset.carrierFreq);
    setBrainwaveFreq(preset.brainwaveFreq);
    setModulationType(preset.modulationType);
    
    // Stop preview when preset changes (modulation type may change)
    if (isPreviewPlaying) {
      stopPreview();
    }
  }, [isPreviewPlaying, stopPreview]);

  const startPreview = useCallback(() => {
    if (!audioContext || isPreviewPlaying) return;

    // Always use current state values, not captured closure values
    const currentControlPoints: BrainwaveControlPoint[] = [
      { time: 0, carrierFreq, brainwaveFreq, amplitude },
      { time: 10, carrierFreq, brainwaveFreq, amplitude } // 10 second preview
    ];

    const oscillator = new BrainwaveOscillatorNode({
      audioContext,
      modulationType,
      controlPoints: currentControlPoints,
      duration: 10,
      trackVolume: amplitude

    });

    oscillator.connect(audioContext.destination);
    oscillator.start();
    
    previewOscillatorRef.current = oscillator;
    setIsPreviewPlaying(true);

    // Auto-stop after 10 seconds
    previewTimeoutRef.current = setTimeout(() => {
      stopPreview();
    }, 10000);
  }, [audioContext, isPreviewPlaying, carrierFreq, brainwaveFreq, amplitude, modulationType, stopPreview]);

  const handleCarrierFreqChange = useCallback((value: number) => {
    setCarrierFreq(value);
    setSelectedPreset('Custom');
    
    // Update parameters smoothly in real-time
    if (isPreviewPlaying && previewOscillatorRef.current) {
      const newControlPoints: BrainwaveControlPoint[] = [
        { time: 0, carrierFreq: value, brainwaveFreq, amplitude },
        { time: 10, carrierFreq: value, brainwaveFreq, amplitude }
      ];
      previewOscillatorRef.current.updateControlPoints(newControlPoints);
    }
  }, [isPreviewPlaying, brainwaveFreq, amplitude]);

  const handleBrainwaveFreqChange = useCallback((value: number) => {
    setBrainwaveFreq(value);
    setSelectedPreset('Custom');
    
    // Update parameters smoothly in real-time
    if (isPreviewPlaying && previewOscillatorRef.current) {
      //console.log('handleBrainwaveFreqChange', value, carrierFreq, amplitude);
      const newControlPoints: BrainwaveControlPoint[] = [
        { time: 0, carrierFreq, brainwaveFreq: value, amplitude },
        { time: 10, carrierFreq, brainwaveFreq: value, amplitude }
      ];
      previewOscillatorRef.current.updateControlPoints(newControlPoints);
    }
  }, [isPreviewPlaying, carrierFreq, amplitude]);

  const handleAmplitudeChange = useCallback((value: number) => {
    setAmplitude(value);
    
    // For amplitude, use setVolume for instant response
    if (isPreviewPlaying && previewOscillatorRef.current) {
      previewOscillatorRef.current.setVolume(value);
    }
  }, [isPreviewPlaying]);

  const handleAddTrack = useCallback(() => {
    // Use full amplitude (1.0) in control points for timeline tracks
    // The track volume will be set separately to the current amplitude value
    const controlPoints: BrainwaveControlPoint[] = [
      { time: 0, carrierFreq, brainwaveFreq, amplitude: 1.0 },
      { time: duration, carrierFreq, brainwaveFreq, amplitude: 1.0 }
    ];

    const settings: BrainwaveSettings = {
      modulationType,
      controlPoints,
      preset: selectedPreset,
      previewVolume: amplitude // Pass the current amplitude as the intended volume
    };

    onAddTrack(settings);
    
    // Stop preview if playing
    if (isPreviewPlaying) {
      stopPreview();
    }
  }, [carrierFreq, brainwaveFreq, amplitude, modulationType, duration, selectedPreset, onAddTrack, isPreviewPlaying, stopPreview]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getFrequencyRange = (freq: number): string => {
    if (freq < 4) return 'Delta';
    if (freq < 8) return 'Theta';
    if (freq < 14) return 'Alpha';
    if (freq < 30) return 'Beta';
    return 'Gamma';
  };

  return (
    <GeneratorContainer>
      <GeneratorHeader>
        <GeneratorTitle>
          <Icon icon={FaBrain} />
          Brainwave Entrainment Generator
        </GeneratorTitle>
        <PreviewButton 
          $isPlaying={isPreviewPlaying}
          onClick={isPreviewPlaying ? stopPreview : startPreview}
          disabled={!audioContext}
        >
          <Icon icon={isPreviewPlaying ? FaStop : FaPlay} />
          {isPreviewPlaying ? 'Stop Preview' : 'Preview Sound'}
        </PreviewButton>
      </GeneratorHeader>

      <PresetGrid>
        {PRESETS.map(preset => (
          <PresetButton
            key={preset.name}
            $active={selectedPreset === preset.name}
            onClick={() => handlePresetSelect(preset)}
          >
            {preset.name}
            <small>{preset.brainwaveFreq} Hz</small>
          </PresetButton>
        ))}
        <PresetButton
          $active={selectedPreset === 'Custom'}
          onClick={() => setSelectedPreset('Custom')}
        >
          Custom
          <small>Manual</small>
        </PresetButton>
      </PresetGrid>

      <ControlsSection>
        <ControlGroup>
          <ControlLabel>
            Carrier Frequency
            <ControlValue>{carrierFreq} Hz</ControlValue>
          </ControlLabel>
          <Slider
            type="range"
            min="50"
            max="1000"
            value={carrierFreq}
            $isLive={isPreviewPlaying}
            onChange={(e) => handleCarrierFreqChange(Number(e.target.value))}
          />
        </ControlGroup>

        <ControlGroup>
          <ControlLabel>
            Brainwave Frequency
            <ControlValue>{brainwaveFreq} Hz ({getFrequencyRange(brainwaveFreq)})</ControlValue>
          </ControlLabel>
          <Slider
            type="range"
            min="0.5"
            max="44"
            step="0.1"
            value={brainwaveFreq}
            $isLive={isPreviewPlaying}
            onChange={(e) => handleBrainwaveFreqChange(Number(e.target.value))}
          />
        </ControlGroup>

        <ControlGroup>
          <ControlLabel>
            Volume
            <ControlValue>{Math.round(amplitude * 100)}%</ControlValue>
          </ControlLabel>
          <Slider
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={amplitude}
            $isLive={isPreviewPlaying}
            onChange={(e) => handleAmplitudeChange(Number(e.target.value))}
          />
        </ControlGroup>

        <ControlGroup>
          <ControlLabel>
            Modulation Type
          </ControlLabel>
          <Select
            value={modulationType}
            onChange={(e) => {
              const newType = e.target.value as ModulationType;
              setModulationType(newType);
              
              // Stop preview when modulation type changes
              if (isPreviewPlaying) {
                stopPreview();
              }
            }}
          >
            <option value="binaural">Binaural Beats (Different frequency in each ear - best for use with headphones)</option>
            <option value="amplitude">Amplitude Modulation (Volume pulsing - best with single speaker)</option>
            <option value="frequency">Frequency Modulation (Pitch variation - works well, but harsh on the ears)</option>
            <option value="stereo">Stereo Modulation (Alternating ears - works well with stereo speakers or headphones)</option>
            <option value="isochronic">Isochronic Tones (Sharp rhythmic pulses - works well, but most harsh on the ears)</option>
          </Select>
        </ControlGroup>

        <ControlGroup>
          <ControlLabel>
            Track Duration
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DurationInput
                type="number"
                min="30"
                max="3600"
                value={duration}
                onChange={(e) => {
                  setDuration(Number(e.target.value));
                  setIsManuallyEdited(true);
                }}
                title="Track duration in seconds"
              />
              {timelineDuration && timelineDuration > 0 && isManuallyEdited && (
                <button
                  type="button"
                  onClick={() => {
                    setDuration(Math.ceil(timelineDuration));
                    setIsManuallyEdited(false);
                  }}
                  style={{
                    background: '#333',
                    color: '#fff',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '11px',
                    cursor: 'pointer'
                  }}
                  title="Reset to timeline duration"
                >
                  Reset
                </button>
              )}
              <span style={{ color: '#ccc' }}>seconds</span>
            </div>
          </ControlLabel>
          <small style={{ color: '#999', fontSize: '11px' }}>
            {formatDuration(duration)} 
            {isManuallyEdited ? ' (custom)' : timelineDuration && timelineDuration > 0 ? ' (matches timeline)' : ' (default)'}
          </small>
        </ControlGroup>
      </ControlsSection>

      <AddTrackButton onClick={handleAddTrack} disabled={!audioContext}>
        <Icon icon={FaPlus} />
        Add Brainwave Track to Timeline
      </AddTrackButton>
    </GeneratorContainer>
  );
};

export default BrainwaveGeneratorV2;